import React from "react";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { COLORS, CustomDropdownField } from "components";
import {
  InfoIcon,
  InitiativeDetailKpiSectionIcon,
  InitiativeDetailProjectedImprovementIcon,
} from "assets";
import { formatLocaleNumber, formatNullable } from "utils";
import {
  FieldLabel,
  INFO_ICON_STYLES,
  TOOLTIP_STYLES,
  TOOLTIP_TEXT_STYLES,
  TOOLTIP_TITLE_STYLES,
} from "pages/authenticated/accelerate/initiatives-management/components";
import {
  CALCULATED_FALLBACK,
  CHIP_TOOLTIP_STYLES,
  FORMULA_TOOLTIP_STYLES,
} from "components/metric-card";
import { SectionCard } from "../section-card";
import { detailInputSx, detailSelectWrapperSx } from "../../styles";
import { parseNum } from "../../helpers";
import { KpiSectionProps } from "./types";
import { KPI_TOOLTIPS } from "./consts";

export const KpiSection: React.FC<KpiSectionProps> = ({
  form,
  updateField,
  onKpiNameChange,
  derived,
  kpiSelectOptions,
  loadingKpi,
  unitSelectOptions,
  loadingUnits,
}) => (
  <SectionCard
    title="KPI Impact"
    icon={<InitiativeDetailKpiSectionIcon width={36} height={36} />}
  >
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 2, ...detailSelectWrapperSx }}>
          <FieldLabel tooltip={KPI_TOOLTIPS.kpiName}>KPI Name</FieldLabel>
          <CustomDropdownField
            label=""
            options={kpiSelectOptions}
            selectedValue={form.kpiName}
            placeholder={loadingKpi ? "Loading..." : "Select primary impact..."}
            onChange={onKpiNameChange}
            sx={{ width: "100%" }}
          />
        </Box>
        <Box sx={{ flex: 1, ...detailSelectWrapperSx }}>
          <FieldLabel tooltip={KPI_TOOLTIPS.unit}>Unit</FieldLabel>
          <CustomDropdownField
            label=""
            options={unitSelectOptions}
            selectedValue={form.unit}
            placeholder={loadingUnits ? "Loading..." : "Select unit..."}
            onChange={(v) => updateField("unit", v)}
            sx={{ width: "100%" }}
            disabled={!form.kpiName}
          />
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: "#F0F7FF",
          border: "1px solid #BFDBFE",
          borderRadius: "10px",
          boxShadow:
            "inset 0 2px 6px rgba(191,219,254,0.5), 0 1px 3px rgba(0,0,0,0.04)",
          p: 2.5,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <Box sx={{ flex: "1 1 160px" }}>
          <FieldLabel tooltip={KPI_TOOLTIPS.baselineValue}>
            Baseline Value
          </FieldLabel>
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder="0"
            value={form.baselineValue ?? ""}
            onChange={(e) =>
              updateField("baselineValue", parseNum(e.target.value))
            }
            sx={detailInputSx}
          />
        </Box>
        <Box sx={{ flex: "1 1 160px" }}>
          <FieldLabel tooltip={KPI_TOOLTIPS.expectedAnnualImprovement}>
            Expected Annual Improvement
          </FieldLabel>
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder="0"
            value={form.expectedAnnualImprovement ?? ""}
            onChange={(e) =>
              updateField("expectedAnnualImprovement", parseNum(e.target.value))
            }
            sx={detailInputSx}
          />
        </Box>
        <Box sx={{ flex: "1 1 160px" }}>
          <FieldLabel tooltip={KPI_TOOLTIPS.direction}>Direction</FieldLabel>
          <RadioGroup
            row
            value={form.direction}
            onChange={(e) => updateField("direction", e.target.value)}
          >
            <FormControlLabel
              value="REDUCTION"
              control={<Radio size="small" />}
              label="Reduction"
            />
            <FormControlLabel
              value="INCREASE"
              control={<Radio size="small" />}
              label="Increase"
            />
          </RadioGroup>
        </Box>
      </Box>

      {/* Projected Improvement — read-only, auto-calculated */}
      <Box
        sx={{
          backgroundColor: "#F8FAFC",
          border: "1px solid #E8F5E9",
          borderRadius: "10px",
          boxShadow:
            "0 1px 4px rgba(0,0,0,0.05), 0 0 0 1px rgba(129,214,71,0.12)",
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <InitiativeDetailProjectedImprovementIcon width={44} height={44} />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "#94A3B8",
                  fontWeight: 600,
                  mb: 0.25,
                }}
              >
                Projected Improvement
              </Typography>
              <Tooltip
                title={
                  typeof KPI_TOOLTIPS.projectedImprovement !== "string" && (
                    <Box>
                      <Typography sx={TOOLTIP_TITLE_STYLES}>
                        {KPI_TOOLTIPS.projectedImprovement.title}
                      </Typography>
                      <Typography sx={TOOLTIP_TEXT_STYLES}>
                        {KPI_TOOLTIPS.projectedImprovement.text}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        {KPI_TOOLTIPS.projectedImprovement.formula && (
                          <Box sx={FORMULA_TOOLTIP_STYLES(CALCULATED_FALLBACK)}>
                            {KPI_TOOLTIPS.projectedImprovement.formula}
                          </Box>
                        )}
                        {KPI_TOOLTIPS.projectedImprovement.chip && (
                          <Box sx={CHIP_TOOLTIP_STYLES(CALCULATED_FALLBACK)}>
                            {KPI_TOOLTIPS.projectedImprovement.chip}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )
                }
                slotProps={{ tooltip: { sx: TOOLTIP_STYLES } }}
              >
                <Box component="span" sx={INFO_ICON_STYLES}>
                  <InfoIcon color={COLORS.Romance} />
                </Box>
              </Tooltip>
            </Box>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: 800,
                color: "#81D647",
                lineHeight: 1,
              }}
            >
              {formatNullable(
                derived?.projectedImprovementPct ?? null,
                (v) => `${formatLocaleNumber(v)}%`,
              )}
            </Typography>
          </Box>
        </Box>
        {form.baselineValue != null &&
          derived?.projectedNewKpiValue != null && (
            <Box sx={{ textAlign: "right" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: "0.6875rem",
                    color: "#94A3B8",
                    fontWeight: 600,
                    mb: 0.25,
                  }}
                >
                  From baseline
                </Typography>
                <Tooltip
                  title={
                    typeof KPI_TOOLTIPS.projectedImprovement !== "string" && (
                      <Box>
                        <Typography sx={TOOLTIP_TITLE_STYLES}>
                          {KPI_TOOLTIPS.projectedImprovement.title}
                        </Typography>
                        <Typography sx={TOOLTIP_TEXT_STYLES}>
                          {KPI_TOOLTIPS.projectedImprovement.text}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {KPI_TOOLTIPS.projectedImprovement.formula && (
                            <Box
                              sx={FORMULA_TOOLTIP_STYLES(CALCULATED_FALLBACK)}
                            >
                              {KPI_TOOLTIPS.projectedImprovement.formula}
                            </Box>
                          )}
                          {KPI_TOOLTIPS.projectedImprovement.chip && (
                            <Box sx={CHIP_TOOLTIP_STYLES(CALCULATED_FALLBACK)}>
                              {KPI_TOOLTIPS.projectedImprovement.chip}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )
                  }
                  slotProps={{ tooltip: { sx: TOOLTIP_STYLES } }}
                >
                  <Box component="span" sx={INFO_ICON_STYLES}>
                    <InfoIcon color={COLORS.Romance} />
                  </Box>
                </Tooltip>
              </Box>
              <Typography
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  color: "#1A4252",
                }}
              >
                {formatLocaleNumber(form.baselineValue)} →{" "}
                {formatLocaleNumber(derived.projectedNewKpiValue)}
              </Typography>
              {form.unit && (
                <Typography
                  sx={{ fontSize: "0.6875rem", color: "#94A3B8", mt: 0.25 }}
                >
                  {form.unit}
                </Typography>
              )}
            </Box>
          )}
      </Box>
    </Box>
  </SectionCard>
);
